// src/components/SessionProvider.tsx
import React from "react";
import {
  onAuthStateChanged,
  signOut as fbSignOut,
  type User as FirebaseUser,
  type Auth,
} from "firebase/auth";

export type CustomUser = {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  role?: string;
  portals?: string[];
  managed_structures?: string[];
  other_roles?: Array<{ role: string }>;
};

export type SessionUser = FirebaseUser & Partial<CustomUser>;
export type ResolveProfileFn = (
  uid: string,
  firebaseUser: FirebaseUser
) => Promise<Partial<CustomUser> | null | undefined>;

type SessionContextValue = {
  user: SessionUser | null;
  loading: boolean;
  error: Error | null;
  derivedRoleLabel: string;
  signOut: () => Promise<void>;
};

const SessionContext = React.createContext<SessionContextValue | undefined>(undefined);

type Props = {
  children: React.ReactNode;
  /** Use the SAME auth you export from src/firebase/firebase.ts */
  auth: Auth;
  resolveProfile?: ResolveProfileFn;
};

function deriveRoleLabel(u: Partial<CustomUser> | Partial<FirebaseUser> | null | undefined): string {
  if (!u) return "USER";
  const explicit = (u as CustomUser).role;
  if (explicit) return explicit.toUpperCase();
  const portals = (u as CustomUser).portals;
  const managed = (u as CustomUser).managed_structures;
  if (Array.isArray(portals) && portals.length > 0) return "PORTAL_MANAGER";
  if (Array.isArray(managed) && managed.length > 0) return "STRUCTURE_MANAGER";
  return "USER";
}

export function SessionProvider({ children, auth, resolveProfile }: Props) {
  const [user, setUser] = React.useState<SessionUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const unsub = onAuthStateChanged(
      auth,
      async (fbUser) => {
        setError(null);
        if (!fbUser) {
          setUser(null);
          setLoading(false);
          return;
        }
        try {
          const extra = resolveProfile ? await resolveProfile(fbUser.uid, fbUser) : null;
          setUser(Object.assign({}, fbUser, extra || {}));
        } catch (e: any) {
          setUser(fbUser as SessionUser);
          setError(e instanceof Error ? e : new Error(String(e)));
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setError(err);
        setUser(null);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [auth, resolveProfile]);

  const signOut = React.useCallback(async () => {
    await fbSignOut(auth);
    setUser(null);
  }, [auth]);

  const value = React.useMemo<SessionContextValue>(
    () => ({ user, loading, error, signOut, derivedRoleLabel: deriveRoleLabel(user as any) }),
    [user, loading, error, signOut]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useAuth(): SessionContextValue {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error("useAuth must be used within a SessionProvider");
  return ctx;
}
